import React, { useState, useEffect } from "react";
import { searchTracks } from "../api/spotifyapi";
import "./Home.css";

const Home = ({ onTrackSelect }) => {
  const [searchVal, setSearchVal] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!searchVal.trim()) {
      setSearchResult([]);
      return;
    }
    const fetchTracks = async () => {
      setIsSearching(true);
      try {
        const results = await searchTracks(searchVal);
        setSearchResult(results);
      } catch (err) {
        console.error("Error fetching tracks on home:", err);
        setSearchResult([]);
      } finally {
        setIsSearching(false);
      }
    };
    const searchDebounce = setTimeout(fetchTracks, 500);
    return () => clearTimeout(searchDebounce);
  }, [searchVal]);

  const featuredTracks = [
    {
      id: "0VjIjjs4215IY33CtEPu95",
      name: "Blinding Lights",
      artist: "The Weeknd",
      image: "https://i.scdn.co/image/ab67616d0000b2738863d65503039fd0edc0c17d",
      popularity: 98,
      duration_ms: 200040,
      url: "https://open.spotify.com/track/0VjIjjs4215IY33CtEPu95"
    },
    {
      id: "7tFkWsp76w3k4sttdfv7QY",
      name: "Bohemian Rhapsody",
      artist: "Queen",
      image: "https://i.scdn.co/image/ab67616d0000b273e319b40b24e2e8e22da951db",
      popularity: 84,
      duration_ms: 354320,
      url: "https://open.spotify.com/track/7tFkWsp76w3k4sttdfv7QY"
    },
    {
      id: "5Pqn65lhj5n6T5hDq45V6Y",
      name: "STAY",
      artist: "The Kid LAROI & Justin Bieber",
      image: "https://i.scdn.co/image/ab67616d0000b27341e31d6ea68dc30869a2d679",
      popularity: 89,
      duration_ms: 141805,
      url: "https://open.spotify.com/track/5Pqn65lhj5n6T5hDq45V6Y"
    }
  ];

  const handleHeroSearchFocus = () => {
    // Focus search bar or scroll down
    document.getElementById("hero-search-input")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="home-container">
      <main className="main-content">
        {/* Redesigned Hero Section */}
        {/* Redesigned Hero Section */}
        <section className="hero-section">
          <div className="hero-grid">
            {/* Left Column: Content and Search */}
            <div className="hero-content">
              <div className="hero-badge">
                <span className="badge-pulse"></span>
                Now Synced with Spotify
              </div>
              
              <div className="title-container">
                <h1 className="main-title">
                  <span className="title-gradient">Resonance</span>
                </h1>
                <p className="tagline">Where Music Meets Deep Conversation</p>
                <p className="description">
                  Resonance is the social layer for your Spotify soundtrack. Join the community 
                  discussing track production, analyzing lyrics, and sharing song reviews in real-time.
                </p>
              </div>

              {/* Interactive Hero Search */}
              <div className="hero-search-container">
                <div className="hero-search-box">
                  <svg className="hero-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input
                    id="hero-search-input"
                    type="text"
                    placeholder="Search for a song, artist, or album..."
                    value={searchVal}
                    onChange={(e) => setSearchVal(e.target.value)}
                    autoComplete="off"
                  />
                  {isSearching && <div className="spinner"></div>}
                </div>

                {/* Inline search results */}
                {searchVal.trim() !== "" && (
                  <div className="hero-search-results">
                    {searchResult.length > 0 ? (
                      searchResult.map((track) => (
                        <div
                          key={track.id}
                          className="hero-search-item"
                          onClick={() => onTrackSelect(track)}
                        >
                          <div
                            className="item-cover"
                            style={{ backgroundImage: `url(${track.image || "default-image.jpg"})` }}
                          ></div>
                          <div className="item-info">
                            <div className="item-name">{track.name}</div>
                            <div className="item-artist">{track.artist}</div>
                          </div>
                          <div className="item-action">
                            <span>Discuss</span>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-results">
                        {!isSearching ? "No tracks found. Try a different query." : "Searching Spotify..."}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="cta-container">
                <button
                  className="cta-secondary-button"
                  onClick={handleHeroSearchFocus}
                >
                  Search a Track
                </button>
                <a href="#how-it-works" className="cta-link">
                  See How It Works
                </a>
              </div>
            </div>

            {/* Right Column: Visualizer Graphic Card */}
            <div className="hero-visualizer-container">
              <div className="visualizer-card">
                <div className="visualizer-card-header">
                  <div className="status-dot-active"></div>
                  <span className="visualizer-status">Resonance Player</span>
                  <div className="window-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                
                <div className="visualizer-now-playing">
                  <div className="vinyl-wrapper">
                    <div className="vinyl-disc">
                      <div className="vinyl-label">
                        <div className="vinyl-label-center"></div>
                      </div>
                    </div>
                    <div className="vinyl-needle"></div>
                  </div>
                  
                  <div className="now-playing-info">
                    <span className="now-playing-tag animate__animated animate__pulse animate__infinite">NOW PLAYING ON RESONANCE</span>
                    <h4 className="now-playing-title">After Hours</h4>
                    <p className="now-playing-artist">The Weeknd</p>
                    <div className="now-playing-stats">
                      <span className="indicator-live">● 2.4k listening</span>
                      <span className="threads-count">148 threads</span>
                    </div>
                  </div>
                </div>

                <div className="visualizer-brand-icon-container">
                  <svg viewBox="0 0 24 24" fill="none" className="visualizer-brand-icon">
                    <defs>
                      <linearGradient id="vis-logo-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#1db954" />
                        <stop offset="100%" stopColor="#00f2fe" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M3 10v4M6 6v12M9 10v4M12 3v18M15 8v8M18 5v14M21 10v4"
                      stroke="url(#vis-logo-grad)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                
                <div className="mock-chat-bubble-container">
                  <div className="mock-chat-bubble chat-bubble-1">
                    <span className="bubble-user">@synced_mind</span>
                    <p className="bubble-text">That saxophone bridge at 3:15 hits differently...</p>
                  </div>
                  <div className="mock-chat-bubble chat-bubble-2">
                    <span className="bubble-user">@audio_guru</span>
                    <p className="bubble-text">Production is clean. Massive synthesizers!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Discussions */}
        <section className="featured-section">
          <div className="section-header">
            <h2>Trending Discussions</h2>
            <p>Jump right into these popular tracks and see what others are saying.</p>
          </div>

          <div className="featured-grid">
            {featuredTracks.map((track) => (
              <div
                key={track.id}
                className="featured-card"
                onClick={() => onTrackSelect(track)}
              >
                <div className="card-bg-glow"></div>
                <div
                  className="card-cover"
                  style={{ backgroundImage: `url(${track.image})` }}
                >
                  <div className="play-hover-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                  </div>
                </div>
                <div className="card-content">
                  <span className="card-tag">TRENDING THREAD</span>
                  <h3 className="card-title">{track.name}</h3>
                  <p className="card-artist">{track.artist}</p>
                  <div className="card-meta">
                    <span>Popularity: {track.popularity}%</span>
                    <span>•</span>
                    <span>
                      {Math.floor(track.duration_ms / 60000)}:
                      {Math.floor((track.duration_ms % 60000) / 1000)
                        .toString()
                        .padStart(2, "0")}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="info-section">
          <div className="section-header">
            <h2>Connect on a Deeper Frequency</h2>
            <p>Resonance replaces fragmented social comments with a dedicated forum for music talk.</p>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">01</div>
              <h3>Search & Select</h3>
              <p>Type in any track from Spotify. Access metadata, album art, duration, and popularity scores instantly.</p>
            </div>
            <div className="step-card">
              <div className="step-number">02</div>
              <h3>Launch a Thread</h3>
              <p>Share your musical theories, lyrics breakdowns, sound analysis, or simple appreciation of the track.</p>
            </div>
            <div className="step-card">
              <div className="step-number">03</div>
              <h3>Join the Dialogue</h3>
              <p>Engage with replies, read different perspectives, and connect with fans who love your music.</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="footer-logo-icon">
              <path d="M3 10v4M6 6v12M9 10v4M12 3v18M15 8v8M18 5v14M21 10v4" />
            </svg>
            <span>Resonance</span>
          </div>
          <p className="footer-tagline">Connecting minds through sound.</p>
          <p className="copyright">
            &copy; {new Date().getFullYear()} Resonance. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
